import { useState, type ReactNode } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { Landing } from './Landing'
import { SignIn } from './SignIn'
import { ClaimName, PendingApproval, Revoked } from './ClaimName'

/**
 * Decides whether the app renders at all.
 *
 * Everything the app knows about the team sits behind this: the local store is
 * not even created until a session exists, so a signed-out visitor has no roster
 * in memory to leak.
 *
 * `unconfigured` is the exception. A build with no backend (the bundled sample
 * data) has no accounts to sign into, and locking that mode would make it
 * impossible to work on the UI offline. It is only reachable when
 * VITE_SNAPSHOT_URL is unset, which never happens in a deployed build.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { stage, lookupError, refreshMember, signOut } = useAuth()
  const [signingIn, setSigningIn] = useState(false)

  if (stage === 'loading') {
    return (
      <div className="boot">
        <div className="boot__spinner" />
      </div>
    )
  }

  if (stage === 'unconfigured' || stage === 'ready') return <>{children}</>
  if (stage === 'needs-claim') return <ClaimName />
  if (stage === 'pending-approval') return <PendingApproval />
  if (stage === 'revoked') return <Revoked />

  if (stage === 'unverified') {
    return (
      <div className="landing">
        <div className="landing__card">
          <div className="landing__label">Could not check your access</div>
          <p>
            You are signed in, but we could not confirm which player you are. Nothing is lost. Try again
            in a moment.
          </p>
          {lookupError && <p className="muted small">{lookupError}</p>}
          <button type="button" className="btn btn--primary" onClick={() => void refreshMember()}>
            Try again
          </button>
          <button type="button" className="btn" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </div>
    )
  }

  return signingIn ? (
    <SignIn onBack={() => setSigningIn(false)} />
  ) : (
    <Landing onSignIn={() => setSigningIn(true)} />
  )
}
